import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store';
import { selectIsNavigationEnabled } from '../store/slices/navigationSlice';

interface NavigationGuardProps {
  children: React.ReactNode;
}

// NavigationGuard component to prevent direct URL navigation when disabled
const NavigationGuard: React.FC<NavigationGuardProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isNavigationEnabled = useAppSelector(selectIsNavigationEnabled);

  // Store the last allowed location
  const lastAllowedLocationRef = useRef(location.pathname);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // On initial mount (page refresh or direct access)
    if (isInitialMount.current) {
      // If user is accessing a protected shift-plan or shift-rotation sub-route, redirect to home
      if (location.pathname.startsWith('/shift-plan/') ||
          location.pathname.startsWith('/shift-rotation/')) {
        navigate('/', { replace: true });
        return;
      }

      // For non-protected routes, set as last allowed location
      lastAllowedLocationRef.current = location.pathname;
      isInitialMount.current = false;
      return;
    }

    // If navigation is disabled and the location has changed
    if (!isNavigationEnabled && location.pathname !== lastAllowedLocationRef.current) {
      // Silently navigate back to the last allowed location
      navigate(lastAllowedLocationRef.current, { replace: true });
      return;
    }

    // If navigation is enabled, update the last allowed location
    if (isNavigationEnabled) {
      lastAllowedLocationRef.current = location.pathname;
    }
  }, [location.pathname, isNavigationEnabled, navigate]);

  return <>{children}</>;
};

export default NavigationGuard;
